import posthog from "posthog-js";

/**
 * Telemetry for browser-softphone outbound calls (the patient call modal).
 *
 * Every way an outbound call can fail before SignalWire's call script starts
 * recording leaves no trace: the backend only creates the Call row from the
 * recording webhook, and the component's failure paths end in a generic toast
 * plus a `console.error`, which PostHog session recordings do not capture for
 * every tenant. A clinic reporting "outbound calls error out" was therefore
 * undiagnosable — there was no way to tell a denied microphone from a token
 * failure from a WebRTC dial failure, or even to count the attempts.
 *
 * Events:
 *  - `outbound_call_attempted`: one per dial attempt; the denominator. An
 *    attempt abandoned by closing the modal during the mic prompt or token
 *    fetch emits nothing after this, so `attempted` can exceed
 *    `setup_failed + ended`.
 *  - `outbound_call_setup_failed`: the attempt died before a call existed,
 *    tagged with the `stage` it died in. `@signalwire/js` wraps every dial
 *    failure in a generic `CallCreateError` ("Call creation failed"), so the
 *    wrapped error is reported alongside it as `cause_*` — that is where the
 *    actual failure (e.g. a coded `JSONRPCError`) lives.
 *  - `outbound_call_ended`: a dialed call ended. `browser_leg_connected` and
 *    `callee_joined` separate a media/WebRTC failure (leg never connected)
 *    from a PSTN failure (leg connected, callee never joined).
 *
 * NO PHI. The patient's number and name never appear here. Error messages
 * are included because they are the most diagnostic field, but digit runs
 * long enough to be a phone number are scrubbed first.
 */

export type OutboundCallSetupStage = "microphone" | "token" | "dial";

export type OutboundCallEndedBy = "user" | "remote" | "modal_closed";

type Properties = Record<string, string | number | boolean | null | undefined>;

const MAX_ERROR_MESSAGE_LENGTH = 200;

// Seven or more digits, optionally separated by the punctuation phone
// numbers are written with.
const PHONE_NUMBER_PATTERN = /\+?\d(?:[\s().-]*\d){6,}/g;

type ErrorLike = { name?: unknown; code?: unknown; message?: unknown };

function describeErrorLike(
  value: unknown,
  prefix: "error" | "cause",
): Properties {
  if (value === null || value === undefined) {
    return {
      [`${prefix}_name`]: null,
      [`${prefix}_code`]: null,
      [`${prefix}_message`]: null,
    };
  }
  if (typeof value !== "object") {
    return {
      [`${prefix}_name`]: typeof value,
      [`${prefix}_code`]: null,
      [`${prefix}_message`]: null,
    };
  }
  const { name, code, message } = value as ErrorLike;
  return {
    [`${prefix}_name`]:
      typeof name === "string" ? name : (value.constructor?.name ?? null),
    [`${prefix}_code`]:
      typeof code === "string" || typeof code === "number" ? code : null,
    [`${prefix}_message`]:
      typeof message === "string"
        ? message
            .replace(PHONE_NUMBER_PATTERN, "[redacted]")
            .slice(0, MAX_ERROR_MESSAGE_LENGTH)
        : null,
  };
}

/** Describe an error and one level of what it wraps (`cause`, or the SDK's `error`). */
export function describeOutboundCallError(error: unknown): Properties {
  const wrapped =
    error !== null && typeof error === "object"
      ? ((error as { cause?: unknown; error?: unknown }).cause ??
        (error as { error?: unknown }).error)
      : undefined;
  return {
    ...describeErrorLike(error, "error"),
    ...describeErrorLike(wrapped, "cause"),
  };
}

export function captureOutboundCallAttempted(): void {
  posthog.capture("outbound_call_attempted");
}

export function captureOutboundCallSetupFailed(
  stage: OutboundCallSetupStage,
  error: unknown,
  properties: Properties = {},
): void {
  posthog.capture("outbound_call_setup_failed", {
    stage,
    ...describeOutboundCallError(error),
    ...properties,
  });
}

export function captureOutboundCallEnded(properties: {
  call_id: string;
  ended_by: OutboundCallEndedBy;
  final_status: string | null;
  browser_leg_connected: boolean;
  callee_joined: boolean;
  seconds_since_dial: number;
}): void {
  posthog.capture("outbound_call_ended", properties);
}
